// RSO-change CAS-246 smart login
import i18next from "i18next";
import {rsoSmartLogin} from "./RsoAuthBackend";

import {getProtocolType} from "../utils/AuthUtil";
import {convertBirthDate} from "./Util";

function isMisleadingHonaSuccessMessage(message) {
  if (!message || typeof message !== "string") {
    return false;
  }
  const lower = message.trim().toLowerCase();
  return lower === "success" || (lower.startsWith("[200]") && lower.includes("success"));
}

function normalizeDataRequired(dataRequired) {
  if (Array.isArray(dataRequired)) {
    return dataRequired;
  }
  if (dataRequired && typeof dataRequired === "object") {
    return Object.entries(dataRequired).map(([name, field]) => ({
      name,
      ...(field || {}),
    }));
  }
  return [];
}

// Keeps latest Hona uniqueKey/text when error responses omit user_data.
const honaSessionCache = {uniqueKey: "", text: ""};

function mergeHonaInitialValues(userData, options = {}) {
  const iv = convertBirthDate(userData) || {};
  const src = userData || {};
  const uniqueKey = iv.uniqueKey || iv.honaUniqueKey || src.uniqueKey || "";
  const text = iv.text || iv.honaExpectedText || src.text || "";

  if (options.forceFreshHonaSession) {
    honaSessionCache.uniqueKey = uniqueKey;
    honaSessionCache.text = text;
    return {
      ...iv,
      uniqueKey,
      text,
      nationalId: iv.nationalId || iv.national_id || src.nationalId || "",
    };
  }

  if (uniqueKey) {
    honaSessionCache.uniqueKey = uniqueKey;
  }
  if (text) {
    honaSessionCache.text = text;
  }
  return {
    ...iv,
    uniqueKey: uniqueKey || honaSessionCache.uniqueKey,
    text: text || honaSessionCache.text,
    nationalId: iv.nationalId || iv.national_id || src.nationalId || "",
  };
}

function buildStepInitialValues(stepName, userData, options = {}) {
  if (stepName === "hona_aliveness_detection") {
    const initialValues = mergeHonaInitialValues(userData, options);
    if (options.resetHonaCapture) {
      return {
        ...initialValues,
        personalPictureDataUri: "",
        testVideoDataUri: "",
      };
    }
    return initialValues;
  }
  return convertBirthDate(userData) || {};
}

function resolveSmartLoginStepErrorMessage(currentStep, message) {
  if (currentStep === "hona_aliveness_detection" || isMisleadingHonaSuccessMessage(message)) {
    return i18next.t("general:Liveness verification failed. Please try again.");
  }
  if (message) {
    return message;
  }
  return i18next.t("general:Failed to connect to server");
}

export async function handleNextStep(responseData, application, organization, username, setFormComponent, app, location, oauthParams = {}, serviceUrl = "", returnUrl = "") {
  if (!responseData || !responseData.data) {
    throw new Error("Invalid response data");
  }

  const data = responseData.data;

  if (data.status !== "ok") {
    throw new Error(data.message || "Unknown error");
  }

  const {action, current_step, next_step, message, data_required, next_step_data_required, user_data, state_id, is_next_step_skippable} = data;
  const currentLocation = {
    ...location,
    search: window.location.search,
  };
  const protocolType = getProtocolType(currentLocation, app);

  if (data.status === "error") {
    const errorMessage = resolveSmartLoginStepErrorMessage(current_step, message);
    const dataRequired = normalizeDataRequired(data_required);
    const initialValues = buildStepInitialValues(current_step, user_data, {
      resetHonaCapture: current_step === "hona_aliveness_detection",
      forceFreshHonaSession: current_step === "hona_aliveness_detection",
    });

    setFormComponent({
      type: current_step,
      dataRequired,
      errorMessage,
      initialValues,
      stateId: state_id,
      isNextStepSkippable: is_next_step_skippable ?? false,
      onSubmit: async(formData) => {
        const {step_action, ...stepData} = formData;
        const additionalData = {username, application, organization};
        if (step_action) {additionalData.step_action = step_action;}

        return await rsoSmartLogin(
          stepData,
          additionalData,
          state_id,
          application,
          organization,
          protocolType,
          oauthParams,
          serviceUrl
        );
      },
    });

    return;
  }

  if (data.status !== "ok") {
    throw new Error(data.message || "Unknown error");
  }
  // Handle completed flow
  if (action === "completed" || next_step === "complete") {
    const delegatedAccounts = data.delegatedAccounts;

    if (delegatedAccounts && delegatedAccounts.length > 0) {
      return;
    }

    console.log(`Protocol type: ${protocolType}, user_data: ${JSON.stringify(data.user_data)}`);
    console.log(`user_data: ${JSON.stringify(data.user_data)}`);
    const extractPayload = (jsonResp) => {
      let payload = jsonResp?.user_data?.response;
      if (!payload) {
        payload = jsonResp?.user_data;
        if (!payload) {
          console.log("Unexpected response data - data.user_data empty!?");
          return;
        }
      }
      return payload;
    };
    switch (protocolType) {
    case "cas": {
      const payload = extractPayload(data);
      const st = payload?.data;
      const redirectTarget = payload?.data2;

      if (redirectTarget) {
        const newUrl = new URL(redirectTarget);
        newUrl.searchParams.append("ticket", st);
        console.log("CAS redirect", newUrl.toString());
        window.location.href = newUrl.toString();
        return;
      }
      break;
    }

    case "code": {
      const payload = extractPayload(data);
      const code = payload?.data;
      const concatChar = oauthParams?.redirectUri?.includes("?") ? "&" : "?";
      const clientId = oauthParams?.clientId;
      window.location.href = `${oauthParams.redirectUri}${concatChar}client_id=${clientId}&code=${code}&state=${oauthParams.state}`;
      return;
    }
    }

    window.location.href = "/";
    return;
  }

  if (data.status === "error" && message) {
    const errorMessage = resolveSmartLoginStepErrorMessage(current_step, message);
    const dataRequired = normalizeDataRequired(data_required);
    const initialValues = buildStepInitialValues(current_step, user_data, {
      resetHonaCapture: current_step === "hona_aliveness_detection",
      forceFreshHonaSession: current_step === "hona_aliveness_detection",
    });

    setFormComponent({
      type: current_step,
      dataRequired,
      errorMessage,
      initialValues,
      stateId: state_id,
      isNextStepSkippable: is_next_step_skippable ?? false,
      onSubmit: async(formData) => {
        const {step_action, ...stepData} = formData;
        const additionalData = {username, application, organization};
        if (step_action) {additionalData.step_action = step_action;}

        return await rsoSmartLogin(
          stepData,
          additionalData,
          state_id,
          application,
          organization,
          protocolType,
          oauthParams,
          serviceUrl
        );
      },
    });

    return;
  }

  if (action === "fill_data_required" && data_required) {
    setFormComponent({
      type: current_step,
      dataRequired: data_required,
      initialValues: buildStepInitialValues(current_step, user_data),
      stateId: state_id,
      isNextStepSkippable: is_next_step_skippable ?? false,
      onSubmit: async(formData) => {
        const {step_action, ...stepData} = formData;
        const additionalData = {username, application, organization};
        if (step_action) {additionalData.step_action = step_action;}

        return await rsoSmartLogin(
          stepData,
          additionalData,
          state_id,
          application,
          organization,
          protocolType,
          oauthParams,
          serviceUrl
        );
      },
    });

    return;
  }

  if (action === "proceed") {
    if (next_step === "complete") {
      window.location.href = "/";
      return;
    }

    if (data_required && Object.keys(data_required).length > 0) {
      setFormComponent({
        type: current_step,
        dataRequired: data_required,
        initialValues: buildStepInitialValues(current_step, user_data),
        stateId: state_id,
        isNextStepSkippable: is_next_step_skippable ?? false,
        onSubmit: async(formData) => {
          const {step_action, ...stepData} = formData;
          const additionalData = {username, application, organization};
          if (step_action) {additionalData.step_action = step_action;}

          return await rsoSmartLogin(
            stepData,
            additionalData,
            state_id,
            application,
            organization,
            protocolType,
            oauthParams,
            serviceUrl
          );
        },
      });
      return;
    }

    if (next_step_data_required && Object.keys(next_step_data_required).length > 0) {
      setFormComponent({
        type: next_step,
        dataRequired: next_step_data_required,
        initialValues: buildStepInitialValues(next_step, user_data),
        nextStep: next_step,
        stateId: state_id,
        isNextStepSkippable: is_next_step_skippable ?? false,
        onSubmit: async(formData) => {
          const {step_action, ...stepData} = formData;
          const additionalData = {username, application, organization};
          if (step_action) {additionalData.step_action = step_action;}

          return await rsoSmartLogin(
            stepData,
            additionalData,
            state_id,
            application,
            organization,
            protocolType,
            oauthParams,
            serviceUrl
          );
        },
      });
      return;
    }

    const emptyResponse = await rsoSmartLogin(
      {},
      {
        username,
        application,
        organization,
      },
      state_id,
      application,
      organization,
      protocolType,
      oauthParams,
      serviceUrl
    );

    await handleNextStep(emptyResponse, application, organization, username, setFormComponent, app, currentLocation, oauthParams, serviceUrl, returnUrl);
    return;
  }

  throw new Error(`Unhandled action: ${action} with status: ${data.status}`);
}
